import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  HiringPipelineFilters,
  SavedDashboardList,
  SelectedInterviewFilterColumnsData,
  SelectedJobStatusFilterColumnsData,
  SelectedPipelineVelocityFilterColumnsData,
  SelectedPipelineVelocityDrilldownFilterColumnsData,
} from 'src/types/analytics-genesis';
import * as reduxApi from 'src/utils/redux-api-v2';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {thunkify as flow} from 'src/utils/thunks';

import {fetching, key} from 'src/utils/redux';
import {
  interviewTableColumnsQuery,
  jobStatusTableColumnsQuery,
  pipelineVelocityTableColumnsQuery,
  pipelineVelocityDrilldownTableColumnsQuery,
} from 'src/utils/analytics-genesis';


export const SET_CURRENT_DASHBOARD_ID = 'hiring-pipeline/set-current-dashboard';
export const SET_SELECTED_FILTERS = 'hiring-pipeline/set-selected-filters';
export const SET_SELECTED_DATE_RANGE =
  'hiring-pipeline/set-selected-date-range';
export const CLEAR_SELECTED_FILTERS = 'hiring-pipeline/clear-selected-filters';
export const RECEIVE_SAVED_DASHBOARDS =
  'hiring-pipeline/receive-saved-dashboards';
export const RECEIVE_INTERVIEW_TABLE_COLUMNS =
  'hiring-pipeline/receive-interview-table-columns';
export const RECEIVE_JOB_STATUS_TABLE_COLUMNS =
  'hiring-pipeline/receive-job-status-table-columns';
export const RECEIVE_PIPELINE_VELOCITY_TABLE_COLUMNS =
  'hiring-pipeline/receive-pipeline-velocity-table-columns';
export const RECEIVE_PIPELINE_VELOCITY_DRILLDOWN_TABLE_COLUMNS =
  'hiring-pipeline/receive-pipeline-velocity-drilldown-table-columns';

type SetCurrentDashboardIdAction = {
  type: 'hiring-pipeline/set-current-dashboard';
  payload: string | null | undefined;
};

type SetSelectedFiltersAction = {
  type: 'hiring-pipeline/set-selected-filters';
  payload: HiringPipelineFilters;
};

type SetDateRangeAction = {
  type: 'hiring-pipeline/set-selected-date-range';
  payload: DateRangeWithTimezone;
};

type ReceiveSavedDashboardsAction = {
  type: 'hiring-pipeline/receive-saved-dashboards';
  payload: SavedDashboardList;
};

type ClearSelectedFiltersAction = {
  type: 'hiring-pipeline/clear-selected-filters';
};

type ReceiveInterviewTableColumnAction = {
  type: 'hiring-pipeline/receive-interview-table-columns';
  payload: SelectedInterviewFilterColumnsData;
};

type ReceiveJobStatusTableColumnAction = {
  type: 'hiring-pipeline/receive-job-status-table-columns';
  payload: SelectedJobStatusFilterColumnsData;
};

type ReceivePipelineVelocityTableColumnAction = {
  type: 'hiring-pipeline/receive-pipeline-velocity-table-columns';
  payload: SelectedPipelineVelocityFilterColumnsData;
};

type ReceivePipelineVelocityDrilldownTableColumnAction = {
  type: 'hiring-pipeline/receive-pipeline-velocity-drilldown-table-columns';
  payload: SelectedPipelineVelocityDrilldownFilterColumnsData;
};

export type HiringPipelineActions =
  | SetSelectedFiltersAction
  | ReceiveSavedDashboardsAction
  | SetCurrentDashboardIdAction
  | ClearSelectedFiltersAction
  | SetDateRangeAction
  | ReceiveInterviewTableColumnAction
  | ReceiveJobStatusTableColumnAction
  | ReceivePipelineVelocityTableColumnAction
  | ReceivePipelineVelocityDrilldownTableColumnAction;

export const setCurrentDashboardId = (
  payload: string,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload,
});

export const setSelectedFilters = (
  payload: HiringPipelineFilters,
): SetSelectedFiltersAction => ({
  type: SET_SELECTED_FILTERS,
  payload,
});

export const setDateRange = (
  payload: DateRangeWithTimezone,
): SetDateRangeAction => ({
  type: SET_SELECTED_DATE_RANGE,
  payload,
});

export const receiveSavedDashboards = (
  payload: SavedDashboardList,
): ReceiveSavedDashboardsAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload,
});

export const clearSelectedFilters = (): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
});

export const receiveInterviewTableColumnSelection = (
  payload: SelectedInterviewFilterColumnsData,
): ReceiveInterviewTableColumnAction => ({
  type: RECEIVE_INTERVIEW_TABLE_COLUMNS,
  payload,
});

export const receiveJobStatusTableColumnSelection = (
  payload: SelectedJobStatusFilterColumnsData,
): ReceiveJobStatusTableColumnAction => ({
  type: RECEIVE_JOB_STATUS_TABLE_COLUMNS,
  payload,
});

export const receivePipelineVelocityTableColumnSelection = (
  payload: SelectedPipelineVelocityFilterColumnsData,
): ReceivePipelineVelocityTableColumnAction => ({
  type: RECEIVE_PIPELINE_VELOCITY_TABLE_COLUMNS,
  payload,
});

export const receivePipelineVelocityDrilldownTableColumnSelection = (
  payload: SelectedPipelineVelocityDrilldownFilterColumnsData,
): ReceivePipelineVelocityDrilldownTableColumnAction => ({
  type: RECEIVE_PIPELINE_VELOCITY_DRILLDOWN_TABLE_COLUMNS,
  payload,
});

export const getInterviewTableColumns: () => ThunkAction<unknown> = flow(
  key(() => 'getInterviewTableColumns'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload = await dispatch(
      reduxApi.get(
        'analytics/crm/column-selection',
        interviewTableColumnsQuery,
      ),
    );
    dispatch(receiveInterviewTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching interview table columns:', error);
  }
});

export const saveInterviewTableColumns: (
  columnData: SelectedInterviewFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) => `saveInterviewTableColumns-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(reduxApi.post('analytics/crm/column-selection', columnData, {})),
);

export const getJobStatusTableColumns: () => ThunkAction<unknown> = flow(
  key(() => 'getJobStatusTableColumns'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload = await dispatch(
      reduxApi.get(
        'analytics/crm/column-selection',
        jobStatusTableColumnsQuery,
      ),
    );
    dispatch(receiveJobStatusTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching job status table columns:', error);
  }
});

export const saveJobStatusTableColumns: (
  columnData: SelectedJobStatusFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) => `saveJobStatusTableColumns-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(reduxApi.post('analytics/crm/column-selection', columnData, {})),
);

export const getPipelineVelocityTableColumns: () => ThunkAction<unknown> = flow(
  key(() => 'getPipelineVelocityTableColumns'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload = await dispatch(
      reduxApi.get(
        'analytics/crm/column-selection',
        pipelineVelocityTableColumnsQuery,
      ),
    );
    dispatch(receivePipelineVelocityTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching pipeline velocity table columns:', error);
  }
});

export const savePipelineVelocityTableColumns: (
  columnData: SelectedPipelineVelocityFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) =>
      `savePipelineVelocityTableColumns-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(reduxApi.post('analytics/crm/column-selection', columnData, {})),
);

export const getPipelineVelocityDrilldownTableColumns: (
  jobId: string,
) => ThunkAction<unknown> = flow(
  key(() => 'getPipelineVelocityDrilldownTableColumns'),
  fetching(),
)((jobId: string) => async (dispatch: Dispatch) => {
  try {
    const payload = await dispatch(
      reduxApi.get('analytics/crm/column-selection', {
        ...pipelineVelocityDrilldownTableColumnsQuery,
        table: `pipeline-velocity-candidate-table-${jobId}`,
      }),
    );
    dispatch(receivePipelineVelocityDrilldownTableColumnSelection(payload));
  } catch (error: any) {
    console.error(
      'Error fetching pipeline velocity drilldown table columns:',
      error,
    );
  }
});

export const savePipelineVelocityDrilldownTableColumns: (
  jobId: string,
  columnData: SelectedPipelineVelocityDrilldownFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (jobId, columnData) =>
      `savePipelineVelocityDrilldownTableColumns-${jobId}-${JSON.stringify(
        columnData,
      )}`,
  ),
  fetching(),
)(
  (jobId, columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        'analytics/crm/column-selection',
        {
          ...columnData,
          table: `pipeline-velocity-candidate-table-${jobId}`,
        },
        {},
      ),
    ),
);

export const getSavedDashboards: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.get(`analytics/crm/save-filter`, {
          product: 'crm',
          dashboard: 'hiring-pipeline',
        }),
      );

      const {
        filters,
      }: {
        filters: SavedDashboardList;
      } = response;
      await dispatch(receiveSavedDashboards(filters));

      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {id = '', filters = {}} = defaultDashboard;
        const {
          // @ts-expect-error - TS2339 - Property 'job_id' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_id = [],
          // @ts-expect-error - TS2339 - Property 'job_source' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_source = [],
          // @ts-expect-error - TS2339 - Property 'job_location' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_location = [],
          // @ts-expect-error - TS2339 - Property 'job_department' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_department = [],
          // @ts-expect-error - TS2339 - Property 'job_recruiter' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_recruiter = [],
        } = filters;
        await dispatch(setCurrentDashboardId(id));
        await dispatch(
          setSelectedFilters({
            job_id,
            job_source,
            job_location,
            job_department,
            job_recruiter,
          }),
        );
      }
    } catch (error: any) {
      console.error(
        'Error fetching saved dashboard filter for recruiters:',
        error,
      );
    }
  };
